import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, CATEGORY_META, fmt, fmt2 } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Star, MapPin, Clock, Users, Heart, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

// ---------- ListingGallery ----------
function ListingGallery({ images, title }) {
  return (
    <div className="grid grid-cols-4 gap-2 rounded-3xl overflow-hidden" data-testid="listing-gallery">
      <div className="col-span-4 md:col-span-3 aspect-[16/10] bg-muted overflow-hidden rounded-3xl">
        <img src={images[0]} alt={title} className="h-full w-full object-cover" />
      </div>
      <div className="hidden md:flex col-span-1 flex-col gap-2">
        {images.slice(1, 3).map((src, i) => (
          <div key={`gallery-${src}-${i}`} className="flex-1 bg-muted rounded-2xl overflow-hidden">
            <img src={src} alt="" className="h-full w-full object-cover" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- BookingPanel ----------
function BookingPanel({ listing, guests, setGuests, date, setDate, addToCart, toggleWish, wish }) {
  const lineFinal = (listing.price?.final || 0) * guests;
  return (
    <aside className="lg:col-span-4">
      <div className="lg:sticky lg:top-24 rounded-2xl bg-card border border-border p-5 shadow-[0_10px_30px_-18px_rgba(31,42,68,0.25)]" data-testid="booking-panel">
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-semibold" data-testid="listing-detail-total-price">{fmt(listing.price?.final)}</span>
          <span className="text-sm text-muted-foreground">per person, all-inclusive</span>
        </div>
        <div className="mt-4 grid gap-3">
          <label className="text-sm font-medium">Pick a date
            <select value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2" data-testid="booking-date-select">
              {(listing.availability || [])
                .filter((a, i, arr) => arr.findIndex(x => x.date === a.date) === i)
                .map((a) => (
                  <option key={a.date} value={a.date}>{a.date} — {a.slots - (a.booked || 0)} slots left</option>
              ))}
              {(!listing.availability || listing.availability.length === 0) && <option value="">No slots</option>}
            </select>
          </label>
          <label className="text-sm font-medium">Guests
            <Input type="number" min={1} max={listing.capacity_per_slot || 20} value={guests} onChange={(e) => setGuests(Math.max(1, Number(e.target.value) || 1))} className="mt-1" data-testid="booking-guests-input" />
          </label>
        </div>
        <div className="mt-4 rounded-xl bg-muted border border-border p-3 text-sm flex items-center justify-between" data-testid="listing-total-row">
          <span className="text-muted-foreground">Total ({guests} {guests === 1 ? "guest" : "guests"})</span>
          <span className="font-semibold" data-testid="listing-total-amount">{fmt2(lineFinal)}</span>
        </div>
        <Button className="mt-4 w-full rounded-xl h-11" onClick={addToCart} data-testid="listing-add-to-cart-button">Add to cart</Button>
        <p className="mt-3 text-xs text-muted-foreground">All-inclusive price. No hidden charges.</p>
        {listing.cancel_window_hours !== undefined && (
          <div className="mt-3 rounded-xl bg-muted/60 border border-border p-3 text-xs" data-testid="listing-cancel-policy">
            <div className="font-medium text-foreground">Free cancellation</div>
            <div className="text-muted-foreground">{listing.cancel_policy_text || `Cancel up to ${listing.cancel_window_hours} hours before the trip for a full refund.`}</div>
          </div>
        )}
        <Button variant="ghost" onClick={toggleWish} className="mt-3 w-full rounded-xl" data-testid="listing-wishlist-button">
          <Heart className={`h-4 w-4 mr-2 ${wish ? "fill-rose-500 text-rose-500" : ""}`} /> {wish ? "Saved" : "Save to wishlist"}
        </Button>
      </div>
    </aside>
  );
}

// ---------- InquiryForm ----------
function InquiryForm({ listingId, user, navigate }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/auth/login?redirect=" + encodeURIComponent(window.location.pathname)); return; }
    if (!subject.trim() || !body.trim()) { toast.error("Please fill in subject and message"); return; }
    setSending(true);
    try {
      await api.post(`/messages/listings/${listingId}/inquiries`, { subject, body, buyer_phone: phone || undefined });
      toast.success("Inquiry sent! The vendor will reply soon.");
      setSubject(""); setBody(""); setPhone("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send inquiry"); }
    setSending(false);
  };

  return (
    <div className="mt-10 rounded-2xl bg-card border border-border p-6" data-testid="inquiry-form">
      <div className="flex items-center gap-2 mb-4">
        <MessageSquare className="h-5 w-5 text-[hsl(var(--primary))]" />
        <h3 className="font-semibold text-lg">Ask the vendor</h3>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (e.g. Group discount query)" className="rounded-xl" data-testid="inquiry-subject-input" maxLength={120} />
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Your message to the vendor…" className="w-full rounded-xl border border-border bg-background p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))]" data-testid="inquiry-body-input" maxLength={2000} />
        <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Your phone number (optional, for SMS reply)" type="tel" className="rounded-xl" data-testid="inquiry-phone-input" />
        <Button type="submit" disabled={sending} className="rounded-xl w-full" data-testid="inquiry-submit-btn">
          {sending ? "Sending…" : user ? "Send inquiry" : "Log in to send inquiry"}
        </Button>
      </form>
    </div>
  );
}


// ---------- Main page ----------
export default function ListingDetail() {
  const { listingId } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [guests, setGuests] = useState(1);
  const [wish, setWish] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const r = await api.get(`/listings/${listingId}`);
        setListing(r.data);
        if (r.data.availability?.length) setDate(r.data.availability[0].date);
      } catch (error) {
        console.error("Failed to load listing details:", error);
        toast.error("Failed to load listing. Please try again.");
      }
      setLoading(false);
    };
    load();
  }, [listingId]);

  const addToCart = async () => {
    if (!user) { navigate("/auth/login?redirect=" + encodeURIComponent(window.location.pathname)); return; }
    if (!date) { toast.error("Please pick a date"); return; }
    try {
      await api.post("/cart/items", { listing_id: listing.listing_id, date, guests: Number(guests) });
      toast.success("Added to cart");
    } catch (error) {
      console.error("Failed to add to cart:", error);
      toast.error("Could not add to cart. Please try again.");
    }
  };

  const toggleWish = async () => {
    if (!user) { navigate("/auth/login"); return; }
    try {
      const r = await api.post(`/wishlist/${listing.listing_id}`);
      setWish(r.data.in_wishlist);
      toast.success(r.data.in_wishlist ? "Saved to wishlist" : "Removed from wishlist");
    } catch (error) {
      console.error("Failed to toggle wishlist:", error);
      toast.error("Could not update wishlist. Please try again.");
    }
  };

  if (loading || !listing) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;

  const images = listing.images?.length ? listing.images : ["https://images.unsplash.com/photo-1542051841857-5f90071e7989?w=1600"];
  const meta = CATEGORY_META[listing.category] || CATEGORY_META.explore;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-8" data-testid="listing-detail-page">
      <nav className="text-sm text-muted-foreground mb-4">
        <Link to="/">Home</Link> <span className="mx-1">/</span>
        <Link to={`/categories/${listing.category}`}>{listing.category}</Link> <span className="mx-1">/</span>
        <span className="text-foreground">{listing.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <ListingGallery images={images} title={listing.title} />

          <div className="mt-6 flex items-start justify-between gap-4">
            <div>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${meta.badge}`}>{listing.category}</span>
              <h1 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight" data-testid="listing-detail-title">{listing.title}</h1>
              <div className="mt-2 flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {listing.location}</span>
                <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {listing.duration_hours} hrs</span>
                <span className="inline-flex items-center gap-1"><Users className="h-4 w-4" /> Up to {listing.capacity_per_slot} pax</span>
                {listing.rating_count > 0 && (
                  <span className="inline-flex items-center gap-1">
                    <Star className="h-4 w-4 fill-amber-400 text-amber-500" /> {listing.rating_avg?.toFixed(1)} · {listing.rating_count} reviews
                  </span>
                )}
              </div>
            </div>
          </div>

          <Tabs defaultValue="overview" className="mt-8">
            <TabsList className="bg-muted">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="inclusions" data-testid="tab-inclusions">Inclusions</TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews ({listing.reviews?.length || 0})</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="pt-5">
              <p className="text-base leading-relaxed whitespace-pre-wrap">{listing.description}</p>
              {listing.highlights?.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-semibold mb-2">Highlights</h3>
                  <ul className="list-disc ml-5 text-muted-foreground">
                    {listing.highlights.map((h, i) => <li key={`highlight-${i}-${h.substring(0, 10)}`}>{h}</li>)}
                  </ul>
                </div>
              )}
            </TabsContent>
            <TabsContent value="inclusions" className="pt-5">
              <ul className="grid sm:grid-cols-2 gap-2">
                {(listing.inclusions || []).map((inc, i) => (
                  <li key={`inclusion-${i}-${inc.substring(0, 10)}`} className="rounded-xl bg-card border border-border p-3 text-sm">✓ {inc}</li>
                ))}
              </ul>
            </TabsContent>
            <TabsContent value="reviews" className="pt-5">
              {(listing.reviews || []).length === 0 ? (
                <p className="text-muted-foreground">No reviews yet. Be the first to share your experience.</p>
              ) : (
                <div className="grid gap-3" data-testid="listing-reviews-list">
                  {listing.reviews.map((r) => (
                    <div key={r.review_id} className="rounded-2xl bg-card border border-border p-4" data-testid="review-card">
                      <div className="flex items-center justify-between">
                        <div className="font-semibold">{r.user_name}</div>
                        <div className="inline-flex items-center gap-1 text-sm"><Star className="h-4 w-4 fill-amber-400 text-amber-500" /> {r.rating}</div>
                      </div>
                      {r.title && <div className="font-medium mt-1">{r.title}</div>}
                      <p className="text-sm text-muted-foreground mt-1">{r.body}</p>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        <BookingPanel
          listing={listing}
          guests={guests}
          setGuests={setGuests}
          date={date}
          setDate={setDate}
          addToCart={addToCart}
          toggleWish={toggleWish}
          wish={wish}
        />
      </div>
      <InquiryForm listingId={listingId} user={user} navigate={navigate} />
    </div>
  );
}
